"use client";
import {
  Column,
  Flex,
  Heading,
  Quizz,
  QuizzData,
  Row,
  Separator,
  variables,
} from "politicamente-informados-ds";
import { submitQuizzResults } from "./QuizzAction";
import { useGlobalContext } from "@/lib/globalContext";
import Image from "next/image";
import { useCallback, useMemo, useState } from "react";

export interface IQuizz extends QuizzData {
  quizzID: string;
  quizzName: string;
  data: {
    quadrants: any[];
    parties: any[];
    questions: {
      id: string;
      question: string;
      score_3: number[];
      score_2: number[];
      score_1: number[];
      score_0: number[];
      "score_-1": number[];
      "score_-2": number[];
      "score_-3": number[];
    }[];
  };
}

const BlockQuizz: React.FC<{ attrs: IQuizz }> = ({ attrs }) => {
  const [resultQuadrant, setResultQuadrant] = useState<any>(null);
  const state = useGlobalContext();
  const { dict } = state;

  // Get matched parties based on result quadrant
  const resultParties = useMemo(() => {
    if (!resultQuadrant?.acf?.parties) return [];
    return attrs.data.parties.filter((party) =>
      resultQuadrant.acf.parties.includes(party.id)
    );
  }, [resultQuadrant, attrs.data.parties]);

  // Get other quadrants (excluding the result one)
  const otherQuadrants = useMemo(() => {
    if (!resultQuadrant) return [];
    return attrs.data.quadrants.filter(
      (quad) => quad?.slug !== resultQuadrant?.slug
    );
  }, [attrs.data.quadrants, resultQuadrant]);

  // Get quiz strings from dictionary
  const quizzStrings = useMemo(
    () => ({
      title:
        dict?.quizz?.strings?.title || "O teu quadrante de pensamento é...",
      disagree: dict?.quizz?.strings?.disagree || "Discordo",
      neutral: dict?.quizz?.strings?.neutral || "Neutro",
      agree: dict?.quizz?.strings?.agree || "Concordo",
      dontKnow: dict?.quizz?.strings?.dontKnow || "Não sei",
      goBack: dict?.quizz?.strings?.goBack || "Voltar à pergunta anterior 👈",
      restart: dict?.quizz?.strings?.restart || "Recomeçar 🔄",
    }),
    [dict]
  );

  const handleComplete = useCallback(
    async (results: any) => {
      setResultQuadrant(results);
      const quizData = {
        quizId: attrs.quizzID,
        name: attrs.quizzName || "Quizz",
      };
      try {
        await submitQuizzResults(quizData);
      } catch (error) {
        console.error("Erro ao enviar dados");
      }
    },
    [attrs.quizzID]
  );

  return (
    <Column className="p-0">
      <Column className="pt-8 pb-8 bg-black w-100">
        <Row>
          <Column md={1}></Column>
          <Column md={10}>
            <Row>
              <Column className="p-0">
                <Flex
                  direction="column"
                  gap="1.25rem"
                  align="center"
                  className="color-white"
                >
                  <Heading
                    size="h2"
                    tag="h2"
                    className="text-center text-gradient fw-semibold"
                  >
                    {dict?.quizz?.title || "Vamos a isto?"}
                  </Heading>
                  <Heading size="texts" tag="p" className="text-center">
                    {dict?.quizz?.description?.text || "São"}{" "}
                    <span className="fw-bold">
                      {dict?.quizz?.description?.questions || "25 perguntas"}
                    </span>{" "}
                    {dict?.quizz?.description?.with || "com"}{" "}
                    <span className="fw-bold">
                      {dict?.quizz?.description?.options ||
                        "quatro opções de resposta"}
                    </span>
                    {dict?.quizz?.description?.colon || ":"}{" "}
                    {dict?.quizz?.description?.optionsList ||
                      '"Concordo", "Discordo", "Não concordo nem Discordo" e "Não sei".'}{" "}
                    {dict?.quizz?.description?.instruction ||
                      "Seleciona a que desejares."}
                  </Heading>
                  <Flex
                    direction="column"
                    gap="0.5rem"
                    align="start"
                    className="w-100 color-white"
                  >
                    {Object.entries(
                      dict?.quizz?.instructions || {
                        disagree: {
                          label: "Concordo",
                          description: "Concordas com a afirmação",
                        },
                        neutral: {
                          label: "Neutro",
                          description:
                            "Tens uma opinião sobre o tema, mas não concordas nem discordas completamente",
                        },
                        agree: {
                          label: "Discordo",
                          description: "Discordas da afirmação",
                        },
                        dontKnow: {
                          label: "Não sei",
                          description:
                            "Não tens uma opinião formada ou conhecimento suficiente sobre o tema",
                        },
                      }
                    ).map(([key, instruction]: [string, any]) => (
                      <Heading
                        key={key}
                        size="captions"
                        tag="p"
                        className={key === "dontKnow" ? "mb-4 " : ""}
                      >
                        <span className="fw-semibold">
                          {instruction?.label}
                        </span>{" "}
                        - {instruction?.description}
                      </Heading>
                    ))}
                  </Flex>
                </Flex>
              </Column>
            </Row>
            <Row>
              <Column className="p-0">
                <div className="bg-white radius-lg pt-6 pb-6 pe-4 ps-4 color-gray100 d-flex justify-content-center">
                    <Quizz
                      {...attrs}
                      strings={quizzStrings}
                      onComplete={handleComplete}
                      onReset={() => {
                        setResultQuadrant(null);
                        const quizzElement = document.getElementById("quizz");
                        if (quizzElement) {
                          quizzElement.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                    />
                </div>
              </Column>
            </Row>
          </Column>
          <Column md={1}></Column>
        </Row>
      </Column>
      {resultQuadrant && (
        <>
          <Row>
            <Column md={1}></Column>
            <Column md={10}>
              <Column className="bg-white pt-8 pb-8">
                <Row>
                  <div>
                    <Flex className="color-gray100" gap="1.5rem">
                      <Heading
                        size="h2"
                        tag="h2"
                        className="text-gradient fw-semibold"
                      >
                        {resultQuadrant?.title}
                      </Heading>
                      <Heading size="texts" tag="p">
                        {resultQuadrant?.acf?.description}
                      </Heading>
                      {/* Characteristics */}
                      {resultQuadrant?.acf?.characteristics?.length > 0 && (
                        <Row className="pt-2 pb-2">
                          <Flex direction="column" gap="1.75rem" align="start">
                            <Heading size="h2" tag="h2" className="fw-semibold">
                              {dict?.quizz?.results?.characteristics ||
                                "Principais características"}
                            </Heading>
                            <Flex direction="column" gap="1rem" align="start">
                              {resultQuadrant.acf.characteristics.map(
                                (charac: any, index: number) => (
                                  <div key={index}>
                                    <Heading
                                      size="texts"
                                      tag="p"
                                      className="fw-bold"
                                    >
                                      {charac?.title}
                                    </Heading>
                                    <Heading size="texts" tag="p">
                                      {charac?.content}
                                    </Heading>
                                  </div>
                                )
                              )}
                            </Flex>
                            <Separator
                              height="2px"
                              color={variables.colors.gray100}
                            />
                          </Flex>
                        </Row>
                      )}

                      {/* Result Parties */}
                      {resultParties.length > 0 && (
                        <>
                          <Heading
                            size="h3"
                            tag="h3"
                            className="text-gradient fw-semibold"
                          >
                            {dict?.quizz?.results?.partiesTitle ||
                              "Partidos com este quadrante de pensamento em Portugal:"}
                          </Heading>
                          <Column className="p-0">
                            <Flex direction="column" gap="1rem" align="start">
                              {resultParties.map((party: any) => (
                                <Flex key={party.id} className="mb-4 p-0">
                                  <a
                                    href={party.acf.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      textDecoration: "none",
                                      color: "inherit",
                                    }}
                                  >
                                    <Flex align="center" gap="1rem">
                                      <div style={{ width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                                        <Image
                                          src={party.acf.logo.url}
                                          alt={party.title}
                                          fill
                                        />
                                      </div>
                                      <Heading
                                        size="captions"
                                        tag="p"
                                        className="fw-semibold text-capitalize"
                                      >
                                        {party.title.toLowerCase()}
                                      </Heading>
                                    </Flex>
                                  </a>
                                </Flex>
                              ))}
                            </Flex>
                          </Column>
                        </>
                      )}
                    </Flex>
                  </div>
                </Row>
              </Column>
            </Column>
            <Column md={1}></Column>
          </Row>
          <Column className="bg-black color-white w-100">
            <Row>
              <Column md={1}></Column>
              <Column md={10}>
                <Column className="bg-gray10 pt-8 pb-8">
                  <Row>
                    {/* Other Quadrants */}
                    <Flex
                      className="p-0 align-items-start"
                      gap="1rem"
                      direction="column"
                    >
                      {otherQuadrants.map((quad: any) => (
                        <Flex
                          direction="column"
                          gap="0.75rem"
                          align="start"
                          key={quad?.slug}
                        >
                          <Heading
                            size="h2"
                            tag="h2"
                            className="text-gradient fw-semibold"
                          >
                            {quad?.title}
                          </Heading>
                          <Heading size="texts" tag="p">
                            {quad?.acf?.description}
                          </Heading>
                        </Flex>
                      ))}
                    </Flex>
                  </Row>
                </Column>
              </Column>
              <Column md={1}></Column>
            </Row>
          </Column>
        </>
      )}
    </Column>
  );
};

export default BlockQuizz;
